/**
 * Use case page data: one entry per /use-cases/<slug> route.
 *
 * These pages are BOFU traffic targets. The reader is searching with
 * intent ("AI IC memo builder", "AI market sizing tool"), so each page
 * needs to: (1) confirm Restack does the thing, (2) show how, (3) make
 * the demo CTA easy. Concise > exhaustive.
 *
 * Adding a new use case = one new entry. No new component.
 */

export const useCases = {
  'ai-ic-memo-builder': {
    slug: 'ai-ic-memo-builder',

    metaTitle: 'AI IC Memo Builder: Investment Committee Memos in 20 Minutes',
    metaDescription:
      'Restack builds investment committee memos directly in PowerPoint, researched, cited, on your firm\'s template. From 4 hours of synthesis to a draft in 20 minutes.',

    hero: {
      eyebrow: 'Use case',
      headline: 'AI IC Memo Builder',
      subhead:
        'IC-ready memos in 20 minutes. Researched, cited, brand-compliant, all inside PowerPoint.',
    },

    problem: {
      title: 'The IC memo eats the team\'s evening',
      body:
        'The associate spends three hours pulling comps, deal precedents, and market data. The senior associate spends another hour reformatting to the firm\'s template. The VP redlines until 11pm. By the time the memo lands with the IC, half the analyst\'s week is in it, and it\'s already stale.',
    },

    solution: {
      title: 'Restack builds the memo skeleton in your live deck',
      body:
        'You drop in the deal context. Restack scans up to 100 sources per slide for market data, comps, and precedents, builds the memo on your firm\'s slide master, and cites every figure. The result is a draft that\'s most of the way there in 20 minutes, leaving the team to focus on the strategic narrative, not the formatting.',
    },

    workflow: [
      { step: '1', title: 'Drop in the deal', description: 'Paste the deal description, target company, sector, geography. Restack identifies what to research.' },
      { step: '2', title: 'AI builds the memo skeleton', description: 'Title, executive summary, market overview, target profile, comps, risks, sources. Each slide on your slide master, in your firm\'s palette.' },
      { step: '3', title: 'Review with citations', description: 'Every figure links to its source. Hover for the exact page and paragraph. The IC asks a question. You answer in one click.' },
      { step: '4', title: 'Iterate in plain English', description: '"Make the comps table 8 names instead of 5." "Add a regulatory risk slide." Restack edits directly in your open deck.' },
    ],

    outputs: [
      'Title slide with deal name, sector, date',
      'Executive summary: investment thesis in 3 bullets',
      'Market overview: TAM/SAM/SOM with cited methodology',
      'Target profile: financials, leadership, traction',
      'Comparable transactions: multiples, deal sizes',
      'Risks & mitigants: regulatory, market, execution',
      'Source appendix: page-level citations to every figure',
    ],

    stats: [
      { value: '20 min', label: 'Time to first draft' },
      { value: '50+', label: 'Cited sources per memo' },
      { value: '4-8 hrs', label: 'Saved per IC memo' },
    ],

    closing: 'IC memos shouldn\'t cost the team a week. The research, the formatting, the citation hunt: Restack does the heavy parts in PowerPoint while you keep the strategic thinking.',
  },

  'ai-market-sizing': {
    slug: 'ai-market-sizing',

    metaTitle: 'AI Market Sizing: TAM/SAM/SOM Slides in 15 Minutes',
    metaDescription:
      'Restack builds TAM/SAM/SOM slides with cited methodology, bottom-up plus top-down, directly in PowerPoint. From half a day of research to a slide in 15 minutes.',

    hero: {
      eyebrow: 'Use case',
      headline: 'AI Market Sizing',
      subhead:
        'TAM/SAM/SOM with cited methodology, on your firm\'s template, in 15 minutes.',
    },

    problem: {
      title: 'Market-sizing slides are deceptively expensive',
      body:
        'The numbers themselves are quick. What kills the day is gathering the underlying data, sense-checking it across sources, building the bottom-up logic, formatting the chart on your firm\'s template, and getting the citations right. By the time the slide is shipping-ready, the analyst is on hour five.',
    },

    solution: {
      title: 'Restack runs the bottom-up and top-down, then builds the slide',
      body:
        'You describe the market. Restack pulls market data from up to 100 sources, runs the bottom-up calculation (user base × ARPU × penetration curve), cross-validates against top-down sources, builds the TAM/SAM/SOM slide on your slide master, and cites every figure to a page-and-paragraph reference.',
    },

    workflow: [
      { step: '1', title: 'Describe the market', description: '"Build a market sizing slide for Nordic fintech entry." Restack identifies the relevant geographies, segments, and time horizon.' },
      { step: '2', title: 'AI scans market data', description: 'Up to 100 sources across reports, filings, databases, and APIs. Cross-validated and confidence-scored.' },
      { step: '3', title: 'Bottom-up + top-down', description: 'Restack runs both methodologies and shows where they converge, giving the IC a defensible point estimate, not a single source.' },
      { step: '4', title: 'Slide on your master', description: 'TAM/SAM/SOM bar chart, key insight callout, methodology footnote, source appendix, all on your firm\'s template.' },
    ],

    outputs: [
      'TAM ($X.XB) with sourced methodology',
      'SAM ($X.XB) with serviceable segment definition',
      'SOM ($X00M) with year-1 obtainable share assumption',
      'Revenue projection chart with CAGR annotation',
      'Methodology footnote (bottom-up + top-down)',
      'Key insight callout (e.g., "Market growing 23% YoY, 2x EU average")',
      'Source appendix with page-level citations',
    ],

    stats: [
      { value: '15 min', label: 'Time to first draft' },
      { value: '20+', label: 'Cited sources per slide' },
      { value: '4-5 hrs', label: 'Saved per market-sizing slide' },
    ],

    closing: 'Market-sizing is one of the most-copied, least-loved slides in consulting. Restack does the data hunt, the bottom-up math, and the formatting. You do the framing.',
  },
};

export const useCaseSlugs = Object.keys(useCases);
